import React, {useEffect, useState} from "react";
import { Button, Card, Grid, Stack, Typography } from "@mui/joy";
import IProfile from "../../types/profile.type";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles/ThemeProviderWithVars";
import {TextField} from "@mui/material";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import {useFormik} from "formik";
import * as Yup from "yup";
import {create, update} from "../../services/profile.service";
import showNotification from "../utils/Notify";
import CancelIcon from '@mui/icons-material/Cancel';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import IFile from "../../types/file.type";
import CuitTextField, {isValidCuitFormat} from "../utils/CuitTextField";

interface Props {
  userId: any,
  profile: IProfile | undefined,
}

const PersonalDataForm: React.FC<Props> = (props) => {
  const { userId, profile } = props;
  const [edit, setEdit] = useState(false);

  const initialValues: IProfile = {
    userId: userId,
    cuit: "",
    name: "",
    lastName: "",
    personalEmail: "",
    personalPhone: "",
    workPhone: "",
    profileImage: undefined,
    address: undefined,
    bankAccount: undefined,
    relatives: undefined,
    employmentData: undefined,
    curriculum: undefined,
    medicalExam: undefined,
    epp: undefined,
    dni: undefined,
    drivingLicense: undefined,
    passport: undefined,
    cuil: undefined,
  };

  const validationSchema = Yup.object({
    userId: Yup.string().required('El usuario es requerido'),
    cuit: Yup.string()
      .required('El CUIT es requerido')
      .test(
        'valid-cuit-format',
        'El CUIT debe tener el formato XX-XXXXXXXX-X',
        (value) => !!value && isValidCuitFormat(value)
      ),
    name: Yup.string().required('El Nombre es requerido'),
    lastName: Yup.string().required('El Apellido es requerido'),
    personalEmail: Yup.string().email('Email invalido'),
  });

  const onSubmit = (profile: IProfile) => {
    if (profile.id) {
      update(profile.id, profile)
        .then((response) => {
          showNotification({type: 'success', message: 'Se actualizaron los Datos Basicos.'});
          profile = response.data;
          formik.setValues(profile);
          setEdit(false);
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(profile)
        .then((response) => {
          showNotification({type: 'success', message: 'Se guardaron los Datos Basicos.'});
          profile = response.data;
          formik.setValues(profile);
          setEdit(false);
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const handleEditCancelClick = () => {
    formik.setTouched({});
    if (edit) {
      setEdit(false);
      if (profile) {
        formik.setValues(profile);
      } else {
        formik.resetForm();
      }
    } else {
      setEdit(true);
    }
  };

  useEffect(() => {
    if (profile) {
      formik.setValues(profile);
    }
  }, [profile?.id]);

  return (
    <Card sx={{gap: 0}}>
      <Typography level="h4" startDecorator={<FolderSharedIcon/>}>Datos Basicos</Typography>
      <form className="form" onSubmit={formik.handleSubmit}>
        <Grid container rowSpacing={2} columnSpacing={2} marginBlock={1}>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="name"
                label="Nombre"
                name="name"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                error={!!(formik.touched.name && formik.errors.name) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.name && formik.errors.name) && edit ? (
                <span className="Error-alert">{formik.errors.name}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="lastName"
                label="Apellido"
                name="lastName"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                error={!!(formik.touched.lastName && formik.errors.lastName) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.lastName && formik.errors.lastName) && edit ? (
                <span className="Error-alert">{formik.errors.lastName}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <CuitTextField
                id="cuit"
                name="cuit"
                value={formik.values.cuit}
                onChange={(value) => formik.setFieldValue('cuit', value)}
                onBlur={formik.handleBlur}
                error={!!(formik.touched.cuit && formik.errors.cuit)}
              />
              {(formik.touched.cuit && formik.errors.cuit) && edit ? (
                <span className="Error-alert">{formik.errors.cuit}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="personalEmail"
                label="Email Personal"
                name="personalEmail"
                variant="filled"
                type={"email"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.personalEmail}
                error={!!(formik.touched.personalEmail && formik.errors.personalEmail) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.personalEmail && formik.errors.personalEmail) && edit ? (
                <span className="Error-alert">{formik.errors.personalEmail}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="personalPhone"
                label="Telefono Personal"
                name="personalPhone"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.personalPhone}
                error={!!(formik.touched.personalPhone && formik.errors.personalPhone) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.personalPhone && formik.errors.personalPhone) && edit ? (
                <span className="Error-alert">{formik.errors.personalPhone}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="workPhone"
                label="Telefono Laboral"
                name="workPhone"
                variant="filled"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.workPhone}
                error={!!(formik.touched.workPhone && formik.errors.workPhone) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.workPhone && formik.errors.workPhone) && edit ? (
                <span className="Error-alert">{formik.errors.workPhone}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
          <Button variant="outlined" endDecorator={edit ? <CancelIcon/> : <BorderColorIcon />} onClick={handleEditCancelClick}>{edit ? 'Cancelar' : 'Editar'}</Button>
          <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon/>} disabled={!edit}>Guardar</Button>
        </Stack>
      </form>
    </Card>
  );
};

export default PersonalDataForm;
